'use strict';

const {distinctUntilChanged, filter} = require('rxjs');
const path = require('path');

const {obj} = require('iblokz-data');
const request = require('superagent');
const auth = require('./auth');

const port = ''; // process.env.PORT && process.env.PORT !== '80' ? `:${process.env.PORT}` : '';
const url = `//${location.host || process.env.SERVER_URL || 'localhost'}${port}`;

const initial = {
	query: {
		start: 0,
		limit: 10,
		search: ''
	},
	list: [],
	doc: {},
	focused: false,
	dirty: true
};

/** Public calendar/community lists need more than the admin page size. */
const publicListLimit = {
	events: 200,
	profiles: 100
};

/** Admin events list: show a long newest-first window (no pagination UI yet). */
const adminListLimit = {
	events: 200
};

const reservedPageIds = ['new', 'configure'];

const handleUnauthorized = err => (
	console.log(err),
	[401, 403].indexOf(err.status) > -1
		? auth.actions.forceLogout()
		: state => state
);

const restConfig = require('../../../../config/api/rest.js');

const scrubEmptyObjectIds = (ns, doc) => {
	const schema = (restConfig.collections[ns] && restConfig.collections[ns].schema) || {};
	const out = Object.keys(doc || {}).reduce((acc, key) => {
		const field = schema[key];
		const value = doc[key];
		if (field && field.type === 'ObjectId' && (value === '' || value === undefined || value === null)) {
			return acc;
		}
		acc[key] = value;
		return acc;
	}, {});
	// nested venue.profile on events
	if (out.venue && typeof out.venue === 'object') {
		const venue = Object.assign({}, out.venue);
		if (venue.profile === '' || venue.profile === undefined || venue.profile === null) {
			delete venue.profile;
		}
		out.venue = venue;
	}
	return out;
};

const stripUploadMeta = item => {
	if (!item || typeof item !== 'object') return item;
	const {_file, _fileName, _urlKind, ...rest} = item;
	return rest;
};

const hasUploadFiles = doc => !!(doc && (
	doc._avatarFile
	|| doc._posterFile
	|| ['tracks', 'videos'].some(kind =>
		(doc[kind] || []).some(item => item && item._file)
	)
));

const toFormData = doc => {
	const fd = new FormData();
	const clean = Object.assign({}, doc);
	delete clean._avatarFile;
	delete clean._avatarFileName;
	delete clean._avatarKind;
	delete clean._posterFile;
	delete clean._posterFileName;
	delete clean._posterKind;
	clean.tracks = (doc.tracks || []).map(stripUploadMeta);
	clean.videos = (doc.videos || []).map(stripUploadMeta);
	fd.append('data', JSON.stringify(clean));
	if (doc._avatarFile) fd.append('avatar', doc._avatarFile);
	if (doc._posterFile) fd.append('poster', doc._posterFile);
	(doc.tracks || []).forEach((item, i) => {
		if (item && item._file) fd.append(`tracks[${i}]`, item._file);
	});
	(doc.videos || []).forEach((item, i) => {
		if (item && item._file) fd.append(`videos[${i}]`, item._file);
	});
	return fd;
};

// ns - namespace
const list = ns => (query = {}, ctrl = '', token) => {
	const q = Object.assign({}, query);
	// legacy admin query used `length`; API expects `limit`
	if (q.length != null && q.limit == null) q.limit = q.length;
	delete q.length;
	return request.get(`${url}/api/${path.join(ns, ctrl)}`)
		.query(q)
		.set(token && {'x-access-token': token} || {})
		.then(res => res.body)
		.then(data => state => obj.patch(state, ns,
			Object.assign({}, data, {dirty: false})
		))
		.catch(handleUnauthorized);
};

const query = ns => keyValuePair => state => obj.patch(state, ns, {
	query: Object.assign({}, obj.sub(state, [].concat(ns, 'query')), keyValuePair),
	dirty: true
});

const create = ns => (doc, token, cb = () => {}) => {
	const payload = scrubEmptyObjectIds(ns, doc);
	const req = request.post(`${url}/api/${ns}`)
		.set(token && {'x-access-token': token} || {});
	return (hasUploadFiles(doc) ? req.send(toFormData(payload)) : req.send(payload))
		.then(() => (
			cb(),
			state => obj.patch(state, ns, {dirty: true, doc: {}})
		))
		.catch(handleUnauthorized);
};

const read = ns => (id, token) => request.get(`${url}/api/${ns}/${id}`)
	.set(token && {'x-access-token': token} || {})
	.then(res => res.body)
	.then(doc => state => obj.patch(state, ns, {doc}))
	.catch(handleUnauthorized);

const update = ns => (id, doc, token, cb = () => {}) => {
	const payload = scrubEmptyObjectIds(ns, doc);
	const req = request.put(`${url}/api/${ns}/${id}`)
		.set(token && {'x-access-token': token} || {});
	return (hasUploadFiles(doc) ? req.send(toFormData(payload)) : req.send(payload))
		.then(() => (
			cb(),
			state => obj.patch(state, ns, {dirty: true, doc: {}})
		))
		.catch(handleUnauthorized);
};

const save = ns => (doc, token, cb = () => {}) => doc._id
	? update(ns)(doc._id, doc, token, cb)
	: create(ns)(doc, token, cb);

const _delete = ns => (id, token) => request.delete(`${url}/api/${ns}/${id}`)
	.set(token && {'x-access-token': token} || {})
	.then(() => state => obj.patch(state, ns, {
		dirty: true,
		doc: {},
		list: (obj.sub(state, [ns, 'list']) || []).filter(doc => String(doc._id) !== String(id))
	}))
	.catch(handleUnauthorized);

const reset = ns => () => state => obj.patch(state, ns, {doc: {}});

const filterFn = ns => query => state => obj.patch(state, [ns, 'query'], query);

const actions = {
	initial,
	list,
	query,
	create,
	update,
	save,
	delete: _delete,
	read,
	reset,
	filter: filterFn
};

const applyNs = (o, ns) => obj.map(o, (k, v) => (v instanceof Function) ? v(ns) : v);

// Attach plain resource node into the tree before createState
const attach = (tree, ns) => Object.assign({}, tree, {
	[ns]: applyNs(actions, ns)
});

let subs = [];
let unhook = () => {
	subs.forEach(sub => sub.unsubscribe());
	subs = [];
};

const publicListNs = ['profiles', 'events'];
// front menu uses /calendar for the public events list; home carousel also needs events.
// calendar/home also load profiles so venue.profile refs can resolve without populate.
const publicPathForNs = ns => (ns === 'events'
	? ['events', 'calendar', 'home', 'contact', '']
	: (ns === 'profiles' ? ['profiles', 'calendar', 'home', ''] : [ns]));

const hook = ({state$, actions}) => ns => {
	const isPublicNs = publicListNs.indexOf(ns) > -1;
	const publicPaths = publicPathForNs(ns);
	// load list on login (dirty), or whenever viewing a public collection page
	subs.push(
		state$.pipe(
			distinctUntilChanged((a, b) => [
				a[ns].dirty,
				!!(a.auth && a.auth.user),
				a.router.path[0],
				a.router.page
			].join('|') === [
				b[ns].dirty,
				!!(b.auth && b.auth.user),
				b.router.path[0],
				b.router.page
			].join('|')),
			filter(state => {
				const authed = !!(state.auth && state.auth.user);
				const path0 = state.router.path[0];
				const onPublicPage = isPublicNs && (
					publicPaths.indexOf(path0) > -1
					|| (ns === 'events' && state.router.page === 'home')
				);
				return (state[ns].dirty && authed) || onPublicPage;
			})
		).subscribe(state => {
			const q = Object.assign({}, state[ns].query);
			const path0 = state.router.path[0];
			const page = state.router.page;
			const onPublicPage = isPublicNs && (
				publicPaths.indexOf(path0) > -1
				|| publicPaths.indexOf(page) > -1
				|| (ns === 'events' && (page === 'home' || !path0))
			);
			if (onPublicPage && publicListLimit[ns]) {
				// force public window (don't trust leftover admin/query limit)
				q.limit = publicListLimit[ns];
			}
			// public events: chronological so upcoming aren't buried on page 1 of defaults
			if (ns === 'events' && onPublicPage) {
				q.sort = 'startDateTime';
			}
			// admin events: newest first (recent on top, past toward the bottom)
			if (ns === 'events' && !onPublicPage) {
				if (!q.sort) q.sort = '-startDateTime';
				if (adminListLimit.events) {
					q.limit = Math.max(Number(q.limit) || 0, adminListLimit.events);
				}
			}
			const token = state.auth && state.auth.token;
			actions[ns].list(q, '', token);
		})
	);
	// on edit / public detail load doc
	subs.push(
		state$.pipe(
			distinctUntilChanged((a, b) => a.router.path.join('/') === b.router.path.join('/')),
			filter(state => (
				(state.router.path[0] === 'admin' && state.router.path[1] === ns)
				|| (publicPaths.indexOf(state.router.path[0]) > -1)
			))
		).subscribe(state => {
			const isAdmin = state.router.path[0] === 'admin';
			const pageId = isAdmin
				? state.router.pageId
				: (state.router.path[1] || null);
			if ([null, ''].indexOf(pageId) > -1 || reservedPageIds.find(
				id => pageId && String(pageId).match(id)
			)) {
				if ([null, ''].indexOf(pageId) > -1
					|| (pageId && pageId.split('/').length === 1)
					|| pageId === 'new') {
					if (isAdmin) actions.set([ns], ({doc: {}}));
				}
				return;
			}
			actions[ns].read(
				isAdmin ? state.router.path.slice().pop() : pageId,
				state.auth.token
			);
		})
	);
};

module.exports = {
	actions,
	applyNs,
	attach,
	hook,
	unhook: () => unhook()
};
